/* eslint-disable react-doctor/jsx-no-constructed-context-values, react-doctor/no-multi-comp, react-doctor/no-react19-deprecated-apis */
import {
    createContext,
    type ReactNode,
    useContext,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";

type ExpandableTextContextValue = {
    isExpanded: boolean;
    setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    isTruncated: boolean;
    contentId: string;
    contentRef: React.RefObject<HTMLDivElement | null>;
    setIsTruncated: React.Dispatch<React.SetStateAction<boolean>>;
    maxLines: number;
};

const ExpandableTextContext = createContext<ExpandableTextContextValue | null>(
    null,
);

function useExpandableText() {
    const ctx = useContext(ExpandableTextContext);
    if (!ctx) {
        throw new Error(
            "ExpandableText compound components must be used within <ExpandableText>",
        );
    }
    return ctx;
}

type ExpandableTextProps = {
    children: ReactNode;
    maxLines?: number;
};

export function ExpandableText({
    children,
    maxLines = 3,
}: ExpandableTextProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const contentId = useId();

    return (
        <ExpandableTextContext.Provider
            value={{
                isExpanded,
                setIsExpanded,
                isTruncated,
                contentId,
                contentRef,
                setIsTruncated,
                maxLines,
            }}
        >
            <div>{children}</div>
        </ExpandableTextContext.Provider>
    );
}

type ExpandableTextContentProps = {
    children: ReactNode;
    className?: string;
};

export function ExpandableTextContent({
    children,
    className,
}: ExpandableTextContentProps) {
    const { isExpanded, contentId, contentRef, setIsTruncated, maxLines } =
        useExpandableText();

    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        let cancelled = false;

        const measure = () => {
            if (cancelled) return;

            const currentClamp =
                el.style.getPropertyValue("-webkit-line-clamp");
            const wasUnclamped =
                currentClamp === "unset" || currentClamp === "";

            if (wasUnclamped) {
                el.style.setProperty("-webkit-line-clamp", String(maxLines));
            }

            const clampedHeight = el.clientHeight;

            el.style.setProperty("-webkit-line-clamp", "unset");
            const naturalHeight = el.scrollHeight;

            if (wasUnclamped) {
                el.style.setProperty("-webkit-line-clamp", "unset");
            } else {
                el.style.setProperty("-webkit-line-clamp", String(maxLines));
            }

            setIsTruncated(naturalHeight > clampedHeight + 1);
        };

        measure();

        const ro = new ResizeObserver(measure);
        ro.observe(el);

        document.fonts?.ready?.then(measure);

        return () => {
            cancelled = true;
            ro.disconnect();
        };
    }, [maxLines, contentRef, setIsTruncated]);

    const style: React.CSSProperties = {
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        WebkitLineClamp: isExpanded ? "unset" : maxLines,
    };

    return (
        <div
            id={contentId}
            ref={contentRef}
            className={className}
            style={style}
        >
            {children}
        </div>
    );
}

type ExpandableTextButtonProps = {
    expandedContent?: ReactNode;
    collapsedContent?: ReactNode;
    className?: string;
};

export function ExpandableTextButton({
    expandedContent = "Show less",
    collapsedContent = "Read more",
    className,
}: ExpandableTextButtonProps) {
    const { isExpanded, setIsExpanded, isTruncated, contentId } =
        useExpandableText();

    if (!isTruncated) return null;

    return (
        <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className={className}
            aria-expanded={isExpanded}
            aria-controls={contentId}
        >
            {isExpanded ? expandedContent : collapsedContent}
        </button>
    );
}
